import { useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useRouter } from 'solito/router'

import useExecuteGoogleRecaptcha from 'app/hooks/useExecuteGoogleRecaptcha'
import useFormErrorHandler from 'app/hooks/useFormErrorHandler'
import { useSendGoogleAnalyticsEvent } from 'app/hooks/useSendGoogleAnalyticsEvent'
import { isErrorHandlerType } from 'app/services/ErrorHandler'
import { useUserEmailRegisterMutation } from 'app/services/Query/user/user.mutation'
import { OTHER_BUSINESS_SEGMENT } from 'app/utils/constants/options.constants'
import { USER_ROLE } from 'app/utils/constants/role.constants'
import { generateAgentToken } from 'app/utils/helpers/functions.helpers'
import { SignUpBusinessFormSchema } from 'app/validation/schemas/user.schemas'

const useSignUpBusinessForm = () => {
  const { push } = useRouter()
  const [isReadTerm, setReadTerm] = useState(false)
  const { sendGA4Event } = useSendGoogleAnalyticsEvent()

  const { mutateAsync } = useUserEmailRegisterMutation()

  const methods = useForm<SignUpBusinessFV>({
    resolver: yupResolver(SignUpBusinessFormSchema),
    mode: 'all',
    defaultValues: {
      business_address_suggest: '',
      business_address_attributes: {
        city: '',
        country: '',
        postal_code: '',
        street_name: '',
        street_no: '',
        label: ''
      }
    }
  })

  const { isSubmitting } = methods.formState

  const handleFormError = useFormErrorHandler(methods.setError)
  const executeGoogleRecaptcha = useExecuteGoogleRecaptcha()

  const businessSegment = useWatch({
    name: 'business_segment',
    control: methods.control
  })
  const isOtherSegment = businessSegment === OTHER_BUSINESS_SEGMENT

  const onSubmit = async (values: SignUpBusinessFV) => {
    try {
      if (!executeGoogleRecaptcha) throw new Error()

      const captchaToken = await executeGoogleRecaptcha('onSubmit')
      const agentToken = generateAgentToken()

      const valuesDto = {
        ...values,
        role: USER_ROLE.BUSINESS,
        conditions_accepted: true,
        phone_number: values.phone_number.replace(' ', ''),
        agent_token: agentToken,
        ...(captchaToken && { captcha_token: captchaToken })
      }

      await mutateAsync(valuesDto)

      const verifyLink = `/sign-up/verify?type=email&email=${encodeURIComponent(
        values.email
      )}`

      sendGA4Event({ eventName: 'sign_up', method: 'email' })

      push(verifyLink)
    } catch (error) {
      if (isErrorHandlerType(error)) handleFormError(error?.data)
    }
  }

  return {
    methods,
    setReadTerm,
    isReadTerm,
    handleSubmit: methods.handleSubmit(onSubmit),
    isOtherSegment,
    isSubmitting
  }
}

export default useSignUpBusinessForm
